import React from "react";
import Link from "next/link";
import { css } from "@emotion/react";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "main/javascripts/store";
import { cruiseSelector, setIsCruiseImageModalDisplayed } from "../cruiseSlice";
import { space } from "main/javascripts/styles/base/spaceStyle";
import { borderRadius } from "main/javascripts/styles/base/borderStyle";
import {
  fontSize,
  fontWeight,
  lineHeight,
} from "main/javascripts/styles/base/typographyStyle";
import {
  accentColor,
  textColor,
} from "main/javascripts/styles/base/colorStyle";
import { Icon } from "main/javascripts/components/icon/Icon";
import { moreThanBreakpoint } from "main/javascripts/styles/base/responsiveStyle";
import { CruiseContentTitle } from "main/javascripts/components/cruise/CruiseContentTitle";

export const CruiseVesselInfo: React.FC = (): React.ReactElement => {
  const { t } = useTranslation(["component"]);
  const appDispatch = useAppDispatch();

  const cruise = useAppSelector(cruiseSelector);
  const vessel = cruise.vessel;

  let vesselImages = vessel.vesselImages;
  if (vesselImages && vesselImages.length > 1) {
    vesselImages = [...vesselImages.slice(1), vesselImages[0]];
  }

  const onImageClick = () => {
    appDispatch(setIsCruiseImageModalDisplayed(true));
  };

  const emptyText = <>&mdash;</>;

  return (
    <div css={blockStyle}>
      <CruiseContentTitle>{t("cruise.shipInfo")}</CruiseContentTitle>
      <div css={infoBlockStyle}>
        {vesselImages?.length > 0 && (
          <div css={imageBlockStyle} onClick={onImageClick}>
            <img css={imageStyle} src={vesselImages[0].imageUrl} alt="" />
            <div css={imageCountStyle}>
              <span css={imageCountTextStyle}>{vesselImages.length}</span>{" "}
              <span css={imageCountIconStyle}>
                <Icon styleKey="images" />
              </span>
            </div>
          </div>
        )}
        <div css={detailStyle}>
          <div css={nameStyle}>
            <Link href={vessel.lpUrl}>{vessel.name}</Link>
          </div>
          {vessel.fileUrl && (
            <div css={fileStyle}>
              <a href={vessel.fileUrl} target="_blank" rel="noreferrer">
                {t("cruise.viewDeckPlan")}
              </a>
            </div>
          )}
          {vessel.logoImageUrl && (
            <div css={logoImageBlockStyle}>
              <img css={logoImageStyle} src={vessel.logoImageUrl} alt="" />
            </div>
          )}
          <div css={brandNameStyle}>
            <Link href={vessel.vesselBrand.lpUrl}>
              {vessel.vesselBrand.name}
            </Link>
          </div>
          <div css={dataBlockStyle}>
            <div>
              <div css={dataLabelStyle}>{t("cruise.yearBuilt")}</div>
              <div>{vessel.yearBuilt || emptyText}</div>
            </div>
            <div>
              <div css={dataLabelStyle}>{t("cruise.yearRefurbished")}</div>
              <div>{vessel.yearRefurbished || emptyText}</div>
            </div>
            <div>
              <div css={dataLabelStyle}>{t("cruise.guestCapacity")}</div>
              <div>{vessel.guestCapacity || emptyText}</div>
            </div>
            <div>
              <div css={dataLabelStyle}>{t("cruise.onboardCrewCapacity")}</div>
              <div>{vessel.onboardCrewCapacity || emptyText}</div>
            </div>
            <div>
              <div css={dataLabelStyle}>{t("cruise.lengthInFeet")}</div>
              <div>{vessel.lengthInFeet || emptyText}</div>
            </div>
            <div>
              <div css={dataLabelStyle}>{t("cruise.grossTonnage")}</div>
              <div>{vessel.grossTonnage || emptyText}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const blockStyle = css`
  width: 100%;
`;
const infoBlockStyle = css`
  width: 100%;
  display: grid;
  grid-template-columns: 1fr;
  gap: ${space.atom4x};
  padding: ${space.atom2x} 0;
  ${moreThanBreakpoint("tablet")} {
    grid-template-columns: repeat(auto-fit, minmax(24rem, 1fr));
  }
  ${moreThanBreakpoint("desktop")} {
    grid-template-columns: 1fr;
  }
`;
const imageBlockStyle = css`
  position: relative;
  width: 100%;
  aspect-ratio: 3 / 2;
  border-radius: ${borderRadius.message};
  overflow: hidden;
  cursor: pointer;
  ${moreThanBreakpoint("tablet")} {
    aspect-ratio: 3 / 2;
    margin: 0 auto;
  }
  ${moreThanBreakpoint("desktop")} {
    aspect-ratio: 1 / 1;
  }
`;
const imageStyle = css`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;
const detailStyle = css`
  padding: ${space.atom2x} 0 0;
  ${moreThanBreakpoint("tablet")} {
    padding-top: 0;
  }
`;
const nameStyle = css`
  font-size: ${fontSize.headline};
  line-height: ${lineHeight.headline2};
  font-weight: ${fontWeight.medium};
  padding: 0 0 ${space.atom3x} 0;
  text-align: center;
  a {
    color: ${textColor.primaryDarkColor};
    text-decoration: underline;
    text-underline-offset: ${space.atom};
  }
`;
const fileStyle = css`
  padding: 0 0 ${space.atom2x} 0;
  text-align: center;
  a {
    color: ${accentColor.primaryColor};
    text-decoration: underline;
    text-underline-offset: 0.5rem;
  }
`;
const logoImageBlockStyle = css`
  max-width: 20rem;
  aspect-ratio: 2 / 1;
  margin: 0 auto;
  padding: ${space.atom3x} 0 ${space.atom2x};
  display: flex;
  align-items: center;
  justify-content: center;
`;
const logoImageStyle = css`
  width: 100%;
  height: 100%;
  object-fit: contain;
`;
const brandNameStyle = css`
  font-size: ${fontSize.body};
  line-height: ${lineHeight.body2};
  padding: 0 0 ${space.atom1_5x} 0;
  text-align: center;
  a {
    text-decoration: underline;
    text-underline-offset: ${space.atom};
    color: ${textColor.primaryDarkColor};
  }
`;
const dataBlockStyle = css`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: ${space.atom3x} ${space.atom2x};
  padding: ${space.atom2x} 0;
  max-width: 24rem;
  margin: 0 auto;
  position: relative;
  left: 1rem;
`;
const dataLabelStyle = css`
  font-size: ${fontSize.caption};
  line-height: ${lineHeight.caption2};
  padding-bottom: ${space.atom};
  color: ${textColor.secondaryDarkColor};
`;
const imageCountStyle = css`
  position: absolute;
  top: ${space.atom1_5x};
  right: ${space.atom1_5x};
  background: rgba(0, 0, 0, 0.5);
  padding: 0.5rem 0.75rem;
  color: #fff;
  display: flex;
  align-items: center;
  border-radius: ${borderRadius.normal};
`;
const imageCountTextStyle = css`
  font-size: ${fontSize.body};
  padding-right: ${space.atom0_5x};
`;
const imageCountIconStyle = css`
  position: relative;
  font-size: 1.1875rem;
  display: flex;
  align-items: center;
`;
